import { Clock } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { unzoom } from '@/lib/uiScale'
import { cn } from '@/lib/utils'

/**
 * Čas vždy v 24-hodinovom tvare `HH:MM`.
 *
 * Natívny `<input type="time">` kreslí prehliadač podľa svojho jazyka -
 * v americkom nastavení ukáže 9:00 AM, hoci lyžiarska škola v Európe počíta
 * 9:00 a 13:00. Hodnota ostáva `HH:MM` ako pri natívnom inpute.
 *
 * Písať sa dá voľne („9“, „930“, „9:30“, „9.30“); prečíta sa pri opustení
 * poľa alebo Enteri. Zoznam ponúka časy po `step` minútach.
 *
 * `snap` - zapísaný čas sa zarovná na najbližší dielik `step` (počítané
 * od `origin`, začiatku dňa v kalendári). Rezervácia pri rozdelení hodín
 * po 30 min tak nedostane koniec 11:15. Hodnota, ktorú pole len zobrazuje
 * (stará lekcia), sa nemení - zarovná sa až to, čo niekto zapíše.
 */
export default function TimeInput({
  value,
  onChange,
  step = 15,
  snap = false,
  origin = 0,
  className,
  disabled,
  'aria-label': ariaLabel,
}: {
  value: string
  onChange: (value: string) => void
  /** Krok zoznamu v minútach. */
  step?: number
  /** Zarovnať zapísaný čas na krok. */
  snap?: boolean
  /** Od koľkej (v minútach od polnoci) sa dieliky rátajú. */
  origin?: number
  className?: string
  disabled?: boolean
  'aria-label'?: string
}) {
  const [text, setText] = useState(value ?? '')
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setText(value ?? '')
  }, [value])

  function commit() {
    const trimmed = text.trim()
    if (!trimmed) {
      if (value) onChange('')
      return
    }
    const raw = parseTime(trimmed)
    const parsed = raw && snap ? snapTime(raw, step, origin) : raw
    if (parsed && parsed !== value) onChange(parsed)
    else setText(value ?? '')
  }

  return (
    <div ref={box} className={cn('relative', className)}>
      <input
        type="text"
        inputMode="numeric"
        className="input w-full pr-8 tabular-nums"
        value={text}
        disabled={disabled}
        aria-label={ariaLabel}
        placeholder="hh:mm"
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
        }}
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="absolute inset-y-0 right-0 flex items-center px-2 text-slate-500 hover:text-slate-800"
      >
        <Clock size={15} />
      </button>
      {open ? (
        <TimeList
          anchor={box.current}
          value={value}
          step={step}
          origin={origin}
          onPick={(time) => {
            onChange(time)
            setOpen(false)
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  )
}

/** Zoznam časov - v portáli s pevnou pozíciou, aby ho neorezal modál. */
function TimeList({
  anchor,
  value,
  step,
  origin,
  onPick,
  onClose,
}: {
  anchor: HTMLElement | null
  value: string
  step: number
  origin: number
  onPick: (time: string) => void
  onClose: () => void
}) {
  const panel = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 })
  const first = ((origin % step) + step) % step
  const times = Array.from({ length: Math.ceil((24 * 60 - first) / step) }, (_, i) => format(first + i * step))
  // Zvolený čas, inak najbližší nižší - aby sa zoznam neotváral na polnoci.
  const current = times.filter((time) => time <= (value || '08:00')).pop() ?? times[0]

  useLayoutEffect(() => {
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    const height = 224
    const below = rect.bottom + 4 + height <= window.innerHeight
    setPos({
      top: unzoom(below ? rect.bottom + 4 : Math.max(4, rect.top - 4 - height)),
      left: unzoom(rect.left),
      width: unzoom(rect.width),
    })
    panel.current
      ?.querySelector<HTMLElement>(`[data-time="${current}"]`)
      ?.scrollIntoView({ block: 'center' })
  }, [anchor, current])

  useEffect(() => {
    const away = (e: MouseEvent) => {
      const target = e.target as Node
      if (!panel.current?.contains(target) && !anchor?.contains(target)) onClose()
    }
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    const scroll = (e: Event) => {
      if (!panel.current?.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', key)
    window.addEventListener('scroll', scroll, true)
    window.addEventListener('resize', onClose)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', key)
      window.removeEventListener('scroll', scroll, true)
      window.removeEventListener('resize', onClose)
    }
  }, [anchor, onClose])

  return createPortal(
    <div
      ref={panel}
      style={{ top: pos.top, left: pos.left, minWidth: Math.max(pos.width, 96) }}
      className="fixed z-[1000] max-h-56 overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
    >
      {times.map((time) => (
        <button
          key={time}
          type="button"
          data-time={time}
          onClick={() => onPick(time)}
          className={cn(
            'block w-full px-3 py-1 text-left tabular-nums',
            time === value ? 'bg-action-600 font-semibold text-white' : 'hover:bg-slate-100',
          )}
        >
          {time}
        </button>
      ))}
    </div>,
    document.body,
  )
}

/** Najbližší dielik - „11:15“ pri kroku 30 → „11:30“, „11:10“ → „11:00“. */
export function snapTime(time: string, step: number, origin = 0) {
  const [h, m] = time.split(':').map(Number)
  const minutes = h * 60 + m
  const snapped = origin + Math.round((minutes - origin) / step) * step
  return format(Math.min(Math.max(snapped, 0), 24 * 60 - step))
}

function format(minutes: number) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** „9“ → 09:00, „930“ → 09:30, „9:30“ / „9.30“ / „9 30“ → 09:30. */
export function parseTime(text: string): string | null {
  const parts = text.split(/\D+/).filter(Boolean)
  let hours: number
  let minutes: number
  if (parts.length >= 2) {
    hours = Number(parts[0])
    minutes = Number(parts[1])
  } else if (parts.length === 1) {
    const digits = parts[0]
    if (digits.length <= 2) {
      hours = Number(digits)
      minutes = 0
    } else if (digits.length <= 4) {
      hours = Number(digits.slice(0, -2))
      minutes = Number(digits.slice(-2))
    } else {
      return null
    }
  } else {
    return null
  }
  if (hours > 23 || minutes > 59) return null
  return format(hours * 60 + minutes)
}
